import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import isEqual from 'lodash/isEqual'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import {
  ticketExchangeGetEvents,
  createEditPromoCode,
} from '../../../utilities/api'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'

import { Spinner } from '../../LoadingContainer/Spinner'
import { SelectEvents } from './SelectEvents'
import { Details } from './Details'
import { BackButton } from '../../BackButton'
import { Success } from '../../ModalContent'
import { WarningCard } from '../../WarningCard'
import { LoadingContainer } from '../../LoadingContainer'

import {
  DISCOUNT_CAP_MESSAGE,
  getCodeValidationError,
  getDiscountValidationError,
} from './addPromoCodeValidation'

export default function AddPromoCode({
  organization,
  promoCode,
  priceOpts,
  applyToOpts,
  limitOpts,
  loadPromoCodes,
}) {
  const { closeModal } = useModal()

  const navigate = useNavigate()

  const [step, setStep] = useState(1)

  const [isEditing, setIsEditing] = useState(false)

  const [isLoading, setIsLoading] = useState(true)

  const [initialEditState, setInitialEditState] = useState()

  const [code, setCode] = useState('')

  const [discount, setDiscount] = useState('')

  const [priceOption, setPriceOption] = useState(priceOpts[0]?.value)

  const [initialPriceOption, setInitialPriceOption] = useState(
    priceOption[0]?.value
  )

  const [appliedTo, setAppliedTo] = useState(applyToOpts[0]?.value)

  const [limitOption, setLimitOption] = useState(limitOpts[0]?.value)

  const [limit, setLimit] = useState()

  const [orgEvents, setOrgEvents] = useState([])

  const [isLoadingEvents, setIsLoadingEvents] = useState(true)

  const [eventsToApply, setEventsToApply] = useState([])

  const [isSaving, setIsSaving] = useState(false)

  const [loadingEventsError, setLoadingEventsError] = useState(false)

  const [errors, setErrors] = useState([])

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    variant: 'danger',
    message: '',
  })

  useEffect(() => {
    getEvents()
  }, [])

  useEffect(() => {
    if (!promoCode) {
      // Add mode
      setIsEditing(false)
      setIsLoading(false)
      return
    }

    // Edit mode
    setIsEditing(true)
    setIsLoading(true)

    const discount = promoCode?.amount ?? promoCode?.percent_off
    const limitOption = promoCode?.max_redemptions
      ? limitOpts[1]?.value
      : limitOpts[0]?.value

    setCode(promoCode?.code)
    setPriceOption(promoCode?.priceOption)
    setInitialPriceOption(promoCode?.priceOption)
    setDiscount(discount)
    setAppliedTo(promoCode?.appliedTo)
    setLimitOption(limitOption)
    setLimit(promoCode?.max_redemptions)
    const eventsToApply = promoCode?.events?.map((event) => event?.id)
    setEventsToApply(eventsToApply)
    setInitialEditState({
      code: promoCode?.code,
      priceOption: promoCode?.priceOption,
      discount,
      appliedTo: promoCode?.appliedTo,
      limitOption,
      limit: promoCode?.max_redemptions,
      eventsToApply,
    })
  }, [promoCode])

  useEffect(() => {
    if (isEditing) {
      if (initialEditState) setIsLoading(false)
    }
  }, [isEditing, initialEditState])

  // remove input error when input field changes
  useEffect(() => {
    resetError('code')
  }, [code])

  // remove input error when input field changes
  useEffect(() => {
    resetError('discount')
  }, [discount])

  const getEvents = () => {
    ticketExchangeGetEvents(organization?.uuid)
      .then((res) => {
        setOrgEvents(res.data)
        setIsLoadingEvents(false)
      })
      .catch((err) => {
        console.error(err)
        setLoadingEventsError(true)
        setIsLoadingEvents(false)
      })
  }

  // remove error
  const resetError = (fields) => {
    const foundErrors = findError(fields)
    setErrors(errors?.filter((error) => error?.field !== foundErrors?.field))
  }

  // find error in errors array
  const findError = (fields) => {
    const findObj = (field) => {
      return errors?.find((error) => error.field === field)
    }
    return findObj(fields)
  }

  const getStepOneValidationErrors = () =>
    [
      getCodeValidationError(code),
      getDiscountValidationError(discount, priceOption, priceOpts),
    ].filter(Boolean)

  const handleGoBack = () => {
    setStep(1)
  }

  const handleSave = () => {
    if (step === 1) {
      const stepOneErrors = getStepOneValidationErrors()
      if (stepOneErrors.length > 0) {
        setErrors(stepOneErrors)
        return
      }
      setStep(2)
      return
    }

    if (step === 2) {
      setIsSaving(true)
      let data = {}
      data.name = code
      data.code = code
      data.organization = organization.id
      data.organizationUUID = organization.uuid
      data.appliedTo = appliedTo
      data.priceOption = priceOption
      // decrease by price
      if (priceOption === priceOpts[0]?.value) data.amount = discount
      // decrease by percent
      if (priceOption === priceOpts[1]?.value)
        data.percent_off = Number(discount)
      data.max_redemptions = limit ? Number(limit) : Number.POSITIVE_INFINITY
      data.events = eventsToApply
      // if there are any errors
      if (errors?.length > 0) return
      createEditPromoCode(data)
        .then(async (res) => {
          try {
            await loadPromoCodes() // wait until updated list is loaded
          } catch (err) {
            console.error('Reload failed:', err)
          } finally {
            closeModal()
            setIsSaving(false)
          }
        })
        .catch((err) => {
          console.error('Save failed:', err)
          setIsSaving(false)
        })
    }
  }

  const checkIsDisabled = () => {
    switch (step) {
      case 1:
        return (
          !code ||
          !discount ||
          (limitOption === limitOpts[1]?.value && !limit) ||
          errors.length > 0
        )
      case 2:
        return (
          eventsToApply?.length === 0 ||
          (isEditing &&
            code === initialEditState?.code &&
            priceOption === initialEditState?.priceOption &&
            parseFloat(discount) === parseFloat(initialEditState?.discount) &&
            appliedTo === initialEditState?.appliedTo &&
            limitOption === initialEditState?.limitOption &&
            (limitOption === limitOpts[1]?.value
              ? parseInt(limit) === parseInt(initialEditState?.limit)
              : true) &&
            isEqual(eventsToApply, initialEditState?.eventsToApply))
        )
      default:
        return
    }
  }

  const handleValid = (e) => {
    const { name } = e.target

    switch (name) {
      case 'code': {
        const codeValidationError = getCodeValidationError(e.target.value)
        if (codeValidationError) {
          setErrors((prev) => {
            const withoutCode = prev.filter((error) => error.field !== 'code')
            return [...withoutCode, codeValidationError]
          })
        }
        break
      }
      case 'discount': {
        const discountValidationError = getDiscountValidationError(
          e.target.value,
          priceOption,
          priceOpts
        )
        if (discountValidationError) {
          setErrors((prev) => {
            const withoutDiscount = prev.filter(
              (error) => error.field !== 'discount'
            )
            return [...withoutDiscount, discountValidationError]
          })
        }
        break
      }
      default:
        break
    }
  }

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0 mb-4">
        <Modal.Title className="h4 text-dark">
          {isEditing ? 'Edit' : 'Add'} promo code
        </Modal.Title>
      </Modal.Header>
      <div className="header pt-0 mb-2">
        {step === 1 ? (
          <WarningCard
            text="The promo code must be entered in uppercase, lowercase, or sentence case, must not contain any symbols or spaces, and should not exceed 15 characters in length"
            variant={`${findError('code') ? 'danger' : 'primary'}`}
            marginValue="3"
          />
        ) : (
          <h2 className="normal">
            Choose the events eligible for the promo code.
          </h2>
        )}
      </div>
      {step === 1 ? (
        <>
          {isLoading && isEditing ? (
            <LoadingContainer />
          ) : (
            <Details
              isLoading={isLoading}
              isEditing={isEditing}
              initialEditState={initialEditState}
              priceOpts={priceOpts}
              initialPriceOption={initialPriceOption}
              priceOption={priceOption}
              setPriceOption={setPriceOption}
              applyToOpts={applyToOpts}
              appliedTo={appliedTo}
              setAppliedTo={setAppliedTo}
              limitOpts={limitOpts}
              limitOption={limitOption}
              setLimitOption={setLimitOption}
              limit={limit}
              setLimit={setLimit}
              code={code}
              setCode={setCode}
              discount={discount}
              setDiscount={setDiscount}
              handleValid={handleValid}
              findError={findError}
              errors={errors}
              discountCapMessage={DISCOUNT_CAP_MESSAGE}
            />
          )}
        </>
      ) : (
        <SelectEvents
          orgEvents={orgEvents}
          error={loadingEventsError}
          isLoadingEvents={isLoadingEvents}
          selected={eventsToApply}
          setSelected={setEventsToApply}
        />
      )}
      <Stack
        direction="horizontal"
        className={`btn-group-flex ${step === 2 ? 'justify-content-between' : ''}`}
      >
        {step === 2 && <BackButton size="sm" handleGoBack={handleGoBack} />}
        <Stack direction="horizontal">
          <Button variant="default" size="lg" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            size="lg"
            className="btn-width btn-width-lg--flex-item"
            disabled={checkIsDisabled() || isSaving}
            onClick={handleSave}
          >
            {isSaving ? (
              <Spinner />
            ) : step === 1 ? (
              'Next'
            ) : isEditing ? (
              'Save changes'
            ) : (
              'Add code'
            )}
          </Button>
        </Stack>
      </Stack>
    </>
  )
}
